import {Component} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";
import {WorkflowService} from "../workflow.service";
import {OrderWorkflowService} from "./order-workflow.service";
import {WorkflowStepStatus} from "../models/workflow-step-status.enum";
import {WorkflowTargetInfoModel} from "../models/workflow-target-info.model";
import {UploaderDialogResult} from "../../uploader-wrapper/uploader-dialog.model";
import {WorkflowStep} from "../workflow-step";
import {handleErrors} from "../../misc/handle-errors.decorator";
import {FormControl, FormGroup} from "@angular/forms";
import {DatesAssetsDetailModel, datesAssetsDetailModelFactory} from "./dates-assets-detail.model";
import {ProjectService} from "../../project/project.service";
import {ProjectModel, projectModelFactory} from "../../project/project.model";

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_LIMIT = 10;

@Component({
  templateUrl: "./add-asset-serial-numbers.template.pug",
  providers: [OrderWorkflowService],
})
export class AddAssetSerialNumberComponent extends WorkflowStep {
  orderId: string;
  remainingAssets = 0;
  assets: any[] = [];
  dates: DatesAssetsDetailModel = datesAssetsDetailModelFactory();
  assetsToUpdate: any = {};
  currentPage: number = DEFAULT_PAGE;
  pageSize: number = DEFAULT_PAGE_LIMIT;
  maxPages: number;
  totalCount: number = 0;
  error: Error;
  projectId: string;
  isMigrationActive: boolean = false;

  assetDetailDatesForm = new FormGroup({
    targetDeliveryDate: new FormControl(""),
    actualDeliveryDate: new FormControl(""),
    estimatedDeliveryFullDate: new FormControl(""),
    actualDeliveryFullDate: new FormControl(""),
  });

  ready(workflowTargetInfo: WorkflowTargetInfoModel) {
    if (!workflowTargetInfo.targetId) {
      return;
    }

    this.orderId = workflowTargetInfo.targetId;
    this.loadData();
  }

  @handleErrors()
  async loadData() {
    this.projectId = this.workflowService.workflowConfig.projectId;

    let project: ProjectModel = projectModelFactory();
    project = await this.getProject();
    this.isMigrationActive = project.isMigrationActive;

    await this.loadAssets();
    this.dates = await this.orderWorkflowService.getDatesAssetsDetails(this.orderId);
    this.assetDetailDatesForm.setValue(this.dates);
  }

  private async getProject(): Promise<ProjectModel> {
    return this.projectService.getProjectById(this.workflowTargetInfo.projectId).toPromise();
  }

  @handleErrors()
  private async loadAssets() {
    const {rows, count, assetsWithNoSnMacAddress} = await this.orderWorkflowService.getLeaseAssets(
      this.orderId,
      +(this.pageSize),
      +(this.currentPage) - 1,
    );

    this.assets = rows || [];
    this.maxPages = Math.ceil(count / (+this.pageSize));
    this.totalCount = count;

    this.assets.forEach((asset) => {
      asset.newSerialNumber = this.assetsToUpdate[asset.id] ?
        this.assetsToUpdate[asset.id].serialNumber :
        asset.serialNumber;

      asset.newMacAddress = this.assetsToUpdate[asset.id] ?
        this.assetsToUpdate[asset.id].macAddress :
        asset.macAddress;
    });

    if (assetsWithNoSnMacAddress <= 0) await this.workflowService.setStepStatus(WorkflowStepStatus.Completed);
    this.remainingAssets = assetsWithNoSnMacAddress;
  }

  completed($event) {
    const result = $event["value"] as UploaderDialogResult;

    if (result.responses.length) {
      this.loadData();
    }
  }

  onListControlsChange(event) {
    switch (event["key"]) {
      case "page":
        this.currentPage = event["value"];
        break;
      case "limit":
        this.currentPage = DEFAULT_PAGE;
        this.pageSize = event["value"];
        break;
      default:
        console.info(`Unknown ${event["key"]}`);
        break;
    }

    this.loadData();
  }

  checkUpdates(): void {
    if (!this.assets) return;

    this.assets.forEach((asset) => {
      if (asset.serialNumber !== asset.newSerialNumber || asset.macAddress !== asset.newMacAddress) {
        this.assetsToUpdate[asset.id] = {
          serialNumber: asset.newSerialNumber,
          macAddress: asset.newMacAddress,
        };
      }
    });
  }

  @handleErrors()
  async save(): Promise<void> {
    this.checkUpdates();
    const assetsDetails = [];

    for (const [id, asset] of Object.entries(this.assetsToUpdate)) {
      const {serialNumber, macAddress} = asset as any;
      assetsDetails.push({id, serialNumber, macAddress});
    }

    await this.orderWorkflowService.updateDetails(this.orderId, assetsDetails);

    this.assetsToUpdate = {};
    this.currentPage = DEFAULT_PAGE;

    this.loadData();
  }

  async saveDates(): Promise<void> {
    const changedDates = this.getChanges(this.assetDetailDatesForm);

    await this.orderWorkflowService.updateDatesAssetsDetails(this.orderId, changedDates);
  }

  getChanges(form) {
    const changes = {};
    Object.keys(form.controls).forEach((item) => {
      if (form.controls[item].dirty) {
        changes[item] = form.controls[item].value;
      }
    });
    return changes;
  }

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public workflowService: WorkflowService,
    public orderWorkflowService: OrderWorkflowService,
    private projectService: ProjectService,
  ) {
    super();
  }
}
